<script setup lang="ts">
/**
 * MCP Apps gallery: every ui://smake/* page with the tools that use it, live
 * previews from its fixtures (apps/fixtures) in light/dark and at typical
 * widths — for design review without Claude. Lives in the Tools area: the
 * apps are views of tool results and useful to everyone who uses the tools,
 * not an admin concern.
 */
import { AppWindow, Moon, SearchX, Sun, TriangleAlert } from "@lucide/vue";
import { computed, watch } from "vue";
import { useMcpApp, useMcpApps } from "@/api/apps";
import EmptyState from "@/components/common/EmptyState.vue";
import ErrorState from "@/components/common/ErrorState.vue";
import ListSkeleton from "@/components/common/ListSkeleton.vue";
import PageHeader from "@/components/common/PageHeader.vue";
import McpAppFrame from "@/components/apps/McpAppFrame.vue";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { useRouteQuery } from "@/composables/useRouteQuery";
import { useTheme } from "@/composables/useTheme";
import { t } from "@/copy/strings";
import { cn } from "@/lib/utils";

definePage({ meta: { title: "MCP Apps", layout: "full" } });

const WIDTHS = ["380", "600", "900"] as const;

const apps = useMcpApps();
const { isDark } = useTheme();
const selected = useRouteQuery("app");
const fixtureName = useRouteQuery("fixture");
const themeParam = useRouteQuery("theme");
const widthParam = useRouteQuery("width", "600");

const current = computed(() => apps.data.value?.find((a) => a.name === selected.value) ?? apps.data.value?.[0] ?? null);
const page = useMcpApp(() => current.value?.name);
const fixture = computed(() => page.data.value?.fixtures.find((f) => f.name === fixtureName.value) ?? page.data.value?.fixtures[0] ?? null);
const theme = computed<"light" | "dark">({
  get: () => (themeParam.value === "light" || themeParam.value === "dark" ? themeParam.value : isDark.value ? "dark" : "light"),
  set: (v) => (themeParam.value = v),
});
const width = computed({
  get: () => (WIDTHS.includes(widthParam.value as (typeof WIDTHS)[number]) ? widthParam.value : "600"),
  set: (v: string) => (widthParam.value = v),
});
const fixtureModel = computed({ get: () => fixture.value?.name ?? "", set: (v: string) => (fixtureName.value = v) });

// A different app starts with its default fixture.
watch(
  () => current.value?.name,
  (name, old) => {
    if (old && name !== old) fixtureName.value = "";
  },
);

function select(name: string) {
  selected.value = name;
}

const result = computed(() =>
  fixture.value ? { isError: false, content: [{ type: "text", text: `${current.value?.title}: ${fixture.value.title}` }], structuredContent: fixture.value.data } : null,
);
</script>

<template>
  <div>
    <PageHeader :title="t.apps.title" :description="t.apps.description" :crumbs="[{ label: t.tools.title, to: '/tools' }, { label: t.apps.title }]" />

    <ListSkeleton v-if="apps.isPending.value" :rows="4" />
    <ErrorState v-else-if="apps.error.value" :error="apps.error.value" @retry="apps.refetch()" />
    <EmptyState v-else-if="!current" :icon="SearchX" :title="t.apps.empty" />

    <div v-else class="grid gap-6 lg:grid-cols-[17rem_minmax(0,1fr)]">
      <!-- Narrow screens: pick the app from a list box. -->
      <Select :model-value="current.name" @update:model-value="(v) => select(String(v))">
        <SelectTrigger class="w-full lg:hidden" :aria-label="t.apps.title"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem v-for="app in apps.data.value" :key="app.name" :value="app.name">{{ app.title }}</SelectItem>
        </SelectContent>
      </Select>
      <nav :aria-label="t.apps.title" class="surface hidden flex-col gap-0.5 self-start p-1.5 lg:sticky lg:top-20 lg:flex">
        <button
          v-for="app in apps.data.value"
          :key="app.name"
          type="button"
          :aria-current="app.name === current.name ? 'page' : undefined"
          :class="
            cn(
              'flex flex-col items-start gap-0.5 rounded-lg px-3 py-2 text-left text-sm transition-colors',
              app.name === current.name ? 'bg-state-selected text-foreground' : 'hover:bg-state-hover active:bg-state-press',
            )
          "
          @click="select(app.name)"
        >
          <span class="font-medium">{{ app.title }}</span>
          <span class="font-mono text-xs text-muted-foreground">{{ app.name }}</span>
        </button>
      </nav>

      <section class="flex min-w-0 flex-col gap-4" aria-labelledby="app-heading">
        <div class="flex flex-col gap-2">
          <div class="flex flex-wrap items-center gap-2">
            <AppWindow class="size-4 text-muted-foreground" aria-hidden="true" />
            <h2 id="app-heading" class="section-title">{{ current.title }}</h2>
            <span class="font-mono text-xs text-muted-foreground">{{ current.uri }}</span>
          </div>
          <p class="max-w-3xl text-sm text-muted-foreground">{{ current.description }}</p>
          <div class="flex flex-wrap items-center gap-1.5 text-sm">
            <span class="eyebrow mr-1">{{ t.apps.usedBy }}</span>
            <template v-for="tool in current.tools" :key="tool.name">
              <RouterLink v-if="tool.available" :to="`/tools/${tool.name}`" class="rounded-md">
                <Badge variant="outline" class="font-mono transition-colors hover:border-border-strong hover:bg-state-hover">{{ tool.name }}</Badge>
              </RouterLink>
              <Badge v-else variant="secondary" class="font-mono text-muted-foreground" :title="tool.title">{{ tool.name }}</Badge>
            </template>
            <span v-if="!current.tools.length" class="text-muted-foreground">{{ t.apps.noTools }}</span>
          </div>
        </div>

        <div class="surface flex flex-wrap items-end gap-x-5 gap-y-3 p-3 sm:p-4">
          <label class="flex flex-col gap-1.5">
            <span class="text-xs font-medium text-muted-foreground">{{ t.apps.fixture }}</span>
            <Select v-model="fixtureModel" :disabled="!page.data.value?.fixtures.length">
              <SelectTrigger class="w-56" size="sm" :aria-label="t.apps.fixture"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem v-for="f in page.data.value?.fixtures ?? []" :key="f.name" :value="f.name">{{ f.title }}</SelectItem>
              </SelectContent>
            </Select>
          </label>
          <div class="flex flex-col gap-1.5">
            <span id="theme-label" class="text-xs font-medium text-muted-foreground">{{ t.apps.theme }}</span>
            <ToggleGroup v-model="theme" type="single" size="sm" variant="outline" aria-labelledby="theme-label">
              <ToggleGroupItem value="light" :aria-label="t.apps.light"><Sun class="size-3.5" /> {{ t.apps.light }}</ToggleGroupItem>
              <ToggleGroupItem value="dark" :aria-label="t.apps.dark"><Moon class="size-3.5" /> {{ t.apps.dark }}</ToggleGroupItem>
            </ToggleGroup>
          </div>
          <div class="flex flex-col gap-1.5">
            <span id="width-label" class="text-xs font-medium text-muted-foreground">{{ t.apps.width }}</span>
            <ToggleGroup v-model="width" type="single" size="sm" variant="outline" aria-labelledby="width-label">
              <ToggleGroupItem v-for="w in WIDTHS" :key="w" :value="w" :aria-label="`${w} px`" class="tabular-nums">{{ w }}</ToggleGroupItem>
            </ToggleGroup>
          </div>
        </div>

        <p v-if="!current.built" class="flex items-center gap-2 rounded-lg border border-warning/50 bg-warning-soft/60 p-3 text-sm">
          <TriangleAlert class="size-4 shrink-0 text-warning" aria-hidden="true" /> {{ t.apps.notBuilt }}
        </p>

        <div :class="cn('overflow-x-auto rounded-xl border p-3 sm:p-6', theme === 'dark' ? 'bg-neutral-900' : 'bg-neutral-100')">
          <div class="mx-auto" :style="{ width: `${width}px` }">
            <Skeleton v-if="page.isPending.value" class="h-64 w-full" />
            <ErrorState v-else-if="page.error.value" :error="page.error.value" @retry="page.refetch()" />
            <EmptyState v-else-if="!fixture" compact :icon="AppWindow" :title="t.apps.noFixtures" class="bg-card" />
            <McpAppFrame
              v-else-if="page.data.value"
              :key="`${current.name}:${fixture.name}`"
              :html="page.data.value.html"
              :meta="page.data.value._meta.ui"
              :title="`${current.title} — ${fixture.title}`"
              :tool-result="result"
              :theme="theme"
              messages="preview"
            />
          </div>
        </div>
      </section>
    </div>
  </div>
</template>
